/*
 * Copyright (C) 2025-2026 The Klangmotor Authors (see AUTHORS.MD)
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */

package io.peekandpoke.klang.sprudel.pattern

import io.peekandpoke.klang.common.math.CycleTime
import io.peekandpoke.klang.common.math.CycleTimeSpan
import io.peekandpoke.klang.sprudel.SprudelPattern
import io.peekandpoke.klang.sprudel.SprudelPattern.QueryContext
import io.peekandpoke.klang.sprudel.SprudelPatternEvent
import io.peekandpoke.klang.sprudel.SprudelVoiceValue.Companion.asVoiceValue

/**
 * Reverses the pattern within each cycle (or over multiple cycles based on n).
 *
 * When n <= 1: Standard per-cycle reversal
 * When n > 1: Reverses over n cycles using fast/slow approach
 *
 * @param inner The pattern to reverse
 * @param nProvider Control value provider for the reversal factor (default: 1.0)
 */
internal class ReversePattern(
    val inner: SprudelPattern,
    val nProvider: ControlValueProvider = ControlValueProvider.Static.ONE,
) : SprudelPattern {
    companion object {
        /**
         * Create a ReversePattern with a static n value.
         */
        fun static(inner: SprudelPattern, n: Double = 1.0): ReversePattern {
            return ReversePattern(
                inner = inner,
                nProvider = ControlValueProvider.Static(n.asVoiceValue())
            )
        }

        /**
         * Create a ReversePattern with a control pattern for n.
         */
        fun control(inner: SprudelPattern, nPattern: SprudelPattern): ReversePattern {
            return ReversePattern(
                inner = inner,
                nProvider = ControlValueProvider.Pattern(nPattern)
            )
        }
    }

    override val weight: Double get() = inner.weight

    override val numSteps: Double? get() = inner.numSteps

    override fun estimateCycleDuration(): Double = inner.estimateCycleDuration()

    override fun queryArcContextual(from: CycleTime, to: CycleTime, ctx: QueryContext): List<SprudelPatternEvent> {
        val nEvents = nProvider.queryEvents(from, to, ctx)
        if (nEvents.isEmpty()) return querySimpleReverse(from, to, ctx)

        // Fast-path when provider behaves like a static value over the full range
        if (nEvents.size == 1 && nEvents[0].part.begin == from && nEvents[0].part.end == to) {
            val n = nEvents[0].data.value?.asInt ?: 1
            return if (n <= 1) {
                querySimpleReverse(from, to, ctx)
            } else {
                queryMultiCycleReverse(from, to, ctx, n.toDouble())
            }
        }

        val result = createEventList()

        // For each control event, apply reversal with its n value
        for (nEvent in nEvents) {
            val n = nEvent.data.value?.asInt ?: 1

            val events = if (n <= 1) {
                // Simple per-cycle reversal
                querySimpleReverse(nEvent.part.begin, nEvent.part.end, ctx)
            } else {
                // Multi-cycle reversal
                queryMultiCycleReverse(nEvent.part.begin, nEvent.part.end, ctx, n.toDouble())
            }

            result.addAll(events)
        }

        return result
    }

    private fun querySimpleReverse(from: CycleTime, to: CycleTime, ctx: QueryContext): List<SprudelPatternEvent> {
        val startCycle = from.cycleIndex()
        val endCycle = to.ceilToCycle().cycleIndex()
        val events = createEventList()

        for (c in startCycle..endCycle) {
            val cycle = CycleTime.ofCycleIndex(c)
            val nextCycle = cycle + CycleTime.ONE

            // Intersect query with current cycle
            val intersectStart = from.coerceAtLeast(cycle)
            val intersectEnd = to.coerceAtMost(nextCycle)

            if (intersectEnd > intersectStart) {
                // Reflection pivot for this cycle: maps t -> pivot - t (reverses within the cycle).
                val pivot = CycleTime.ONE + (cycle * 2)
                // Map the intersected range to reversed inner time
                val innerTo = pivot - intersectStart
                val innerFrom = pivot - intersectEnd

                inner.queryArcContextual(innerFrom, innerTo, ctx).forEach { ev ->
                    val reversedPart = CycleTimeSpan(begin = pivot - ev.part.end, end = pivot - ev.part.begin)
                    val reversedWhole = ev.whole.let {
                        CycleTimeSpan(begin = pivot - it.end, end = pivot - it.begin)
                    }

                    events.add(ev.copy(part = reversedPart, whole = reversedWhole))
                }
            }
        }

        return events
    }

    private fun queryMultiCycleReverse(
        from: CycleTime,
        to: CycleTime,
        ctx: QueryContext,
        nRat: Double,
    ): List<SprudelPatternEvent> {
        // Multi-cycle reversal using fast/slow approach
        // fast(n).rev().slow(n)
        val fast: TempoModifierPattern =
            TempoModifierPattern.static(source = inner, factor = nRat, invertPattern = true)

        val reversed =
            ReversePattern(inner = fast, nProvider = ControlValueProvider.Static.ONE)

        val slowed: TempoModifierPattern =
            TempoModifierPattern.static(source = reversed, factor = nRat, invertPattern = false)

        return slowed.queryArcContextual(from, to, ctx)
    }
}
